import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASKS_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  firstRunCellCustomRenderer,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
  repeatIntervalCellCustomRenderer,
  filtersUiConfigs,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzAntimalwareOnDemandScanTasksGridDataProvider } from './GzAntimalwareOnDemandScanTasksGridDataProvider';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { GzAntimalwareOnDemandScanTasksFormWebComponent } from './rulesItemForm';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import {
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskScanType,
} from '@nirvana/shared/dtos/types/policies/common';
import { AntimalwareOnDemandScanTaskFormValue } from '../../../../../utils/uiSettings/antimalware/antimalware.types';
import { OverlayPositionX, SelectOption } from '../../../../../../../../ui/gzSelect/types';
import { selectPoliciesState } from '../../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { getPhantom } from '../../../../../redux/actions';
import { PolicyPanelManager } from '../../../../../PolicyPanelManager';
import {
  ON_DEMAND_TASK_ID_FULL_SCAN,
  ON_DEMAND_TASK_ID_QUICK_SCAN,
} from '../../../../../../../common/policyPanel/uiSettings/taskIdTemplate';
import { GzSelectWebComponent } from '../../../../../../../../ui/gzSelect';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASKS_GRID)
export class GzAntimalwareOnDemandScanTasksGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskFormValue,
  GzAntimalwareOnDemandScanTasksGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
    [ColumnKeys.REPEAT_INTERVAL]: repeatIntervalCellCustomRenderer,
    [ColumnKeys.FIRST_RUN]: firstRunCellCustomRenderer,
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
  protected filtersUiConfigs = filtersUiConfigs();

  protected hasSelectableRows = true;
  protected hasEditRowAction = false;
  protected hasBasicEditRowAction = true;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  @query('#add-button-select')
  protected addButton: GzSelectWebComponent;
  @query('#add-button-empty-state')
  protected addButtonEmptyState: GzSelectWebComponent;
  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected getDataProvider(): GzAntimalwareOnDemandScanTasksGridDataProvider {
    return new GzAntimalwareOnDemandScanTasksGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No tasks')}</gz-text>

      <gz-select
        style="padding-top: 5px;"
        id="add-button-empty-state"
        .options="${this.getAddButtonActions()}"
        closeOnMouseLeave
        @selectionChanged="${this.onAddButtonClick.bind(this)}"
        overlayPositionX="${OverlayPositionX.LEFT}"
      >
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight="${ButtonHeights.SMALL}"
          buttonFontSize="${ButtonFontSizes.SMALL}"
          >${translateFn('Adjust your filters or start adding tasks.')}
        </gz-button>
      </gz-select>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <div style="display: flex; align-items: center;">
        <gz-select
          id="add-button-select"
          .options="${this.getAddButtonActions()}"
          @selectionChanged="${this.onAddButtonClick.bind(this)}"
          overlayPositionX="${OverlayPositionX.LEFT}"
        >
          <gz-button id="add-button" type="${ButtonTypes.OUTLINE}">
            ${translateFn('Add')} <gz-icon name="ds-icon-textbox_decrease" color="primary" size="20"></gz-icon>
          </gz-button>
        </gz-select>

        <gz-button
          id="delete-button"
          type="${ButtonTypes.OUTLINE}"
          color="${ButtonColors.NEGATIVE}"
          ?disabled
          @click="${this.onDeleteSelectedItemsWithDialog.bind(
            this,
            translateFn('Delete scan tasks?'),
            translateFn('You are about to delete the selected scan tasks. This action cannot be undone.')
          )}"
          >${translateFn('Delete')}
        </gz-button>
      </div>
    `;
  }

  private getAddButtonActions(): SelectOption[] {
    const state = selectPoliciesState(store.getState());
    const isNetworkScanDisabled =
      getPhantom(state) ||
      (!Application.Main.product.isCloud() &&
        PolicyPanelManager.getInstance().getAdditionalPolicyInfo(['counters', 'applied'], 0) === 0);

    const items = (this.gridComponent?.dataProvider as GzAntimalwareOnDemandScanTasksGridDataProvider)?.items ?? [];
    const isQuickScanDisabled = items.some(item => item.uniqueId === ON_DEMAND_TASK_ID_QUICK_SCAN);
    const isFullScanDisabled = items.some(item => item.uniqueId === ON_DEMAND_TASK_ID_FULL_SCAN);

    return [
      {
        title: translateFn('Quick scan'),
        value: AntimalwareOnDemandScanTaskScanType.QUICK.toString(),
        disabled: isQuickScanDisabled,
      },
      {
        title: translateFn('Full scan'),
        value: AntimalwareOnDemandScanTaskScanType.FULL.toString(),
        disabled: isFullScanDisabled,
      },
      { title: translateFn('Custom scan'), value: AntimalwareOnDemandScanTaskScanType.CUSTOM.toString() },
      {
        title: translateFn('Network scan'),
        value: AntimalwareOnDemandScanTaskScanType.NETWORK.toString(),
        disabled: isNetworkScanDisabled,
      },
    ];
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: AntimalwareOnDemandScanTaskFormValue): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzAntimalwareOnDemandScanTasksGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: AntimalwareOnDemandScanTaskFormValue,
    newItem: AntimalwareOnDemandScanTaskFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.taskName === oldItem.taskName) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  private getScanTypeWindowTitleById(scanType: AntimalwareOnDemandScanTaskScanType, isAddAction: boolean): string {
    switch (scanType) {
      case AntimalwareOnDemandScanTaskScanType.QUICK:
        return isAddAction ? translateFn('Add quick scan task') : translateFn('Edit quick scan task');
      case AntimalwareOnDemandScanTaskScanType.FULL:
        return isAddAction ? translateFn('Add full scan task') : translateFn('Edit full scan task');
      case AntimalwareOnDemandScanTaskScanType.CUSTOM:
        return isAddAction ? translateFn('Add custom scan task') : translateFn('Edit custom scan task');
      case AntimalwareOnDemandScanTaskScanType.NETWORK:
        return isAddAction ? translateFn('Add network scan task') : translateFn('Edit network scan task');
      default:
        return '';
    }
  }

  private onAddButtonClick(event: CustomEvent): void {
    const scanType = parseInt(event.detail.selected, 10);
    const addRuleCallback = (formValue: AntimalwareOnDemandScanTaskFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzAntimalwareOnDemandScanTasksFormWebComponent(scanType),
      strings: {
        title: this.getScanTypeWindowTitleById(scanType, true),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: AntimalwareOnDemandScanTaskFormValue): void {
    const addRuleCallback = (formValue: AntimalwareOnDemandScanTaskFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzAntimalwareOnDemandScanTasksFormWebComponent(item.scanType, item),
      strings: {
        title: this.getScanTypeWindowTitleById(item.scanType, false),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };
    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.onRuleNameClick(event.detail.rowData.item);
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
    this.addButton.options = this.getAddButtonActions();
    this.addButtonEmptyState.options = this.getAddButtonActions();
  }
}
